import { Alerts } from "../../../../utils/Alerts.js";
import { RPCDocs } from "../../../../services/rpc.js";
import { DEFAULT_IMAGE_URLS } from "../../../../assets/Images/constants.js";
import { useRPCContext } from "../../../../contexts/RPCContext.js";
import { FormikSingleImageFilePicker } from "../../../../components/FormikFields/FormikSingleImageFilePicker.js";
import { Tags } from "../../../Tags/shared/Tags.js";
import { InfoTab } from "../RecordModal/components/InfoTab/index.js";
import { TagsTab } from "./components/TagsTab.js";
import { FilesTab } from "./components/FilesTab.js";
import { useState } from "react";
import { ActionModal, Button, ButtonGroup, Constants, ContextMenu, Icon, Tabs, useContainerDimensionsContext } from "@therms/atalaya";
import { IoWarning } from "react-icons/io5";
import { FaCarSide } from "react-icons/fa";
import { jsx, jsxs } from "react/jsx-runtime";
import classnames from "classnames";
import { MdExpandLess, MdExpandMore, MdInventory } from "react-icons/md";
import { Formik } from "formik";
import { BsFillPersonFill } from "react-icons/bs";
import * as Yup from "yup";
//#region src/modules/Records/shared/RecordForm/index.tsx
var RecordTypes = RPCDocs.Records.CreateRecord.v1.Type;
var DEFAULT_IMAGE_TYPE_MAP = {
	[RecordTypes.Item]: DEFAULT_IMAGE_URLS.RECORD_PROPERTY_PLACEHOLDER,
	[RecordTypes.Person]: DEFAULT_IMAGE_URLS.RECORD_PERSON_PLACEHOLDER,
	[RecordTypes.Vehicle]: DEFAULT_IMAGE_URLS.RECORD_VEHICLE_PLACEHOLDER
};
var ValidationSchema = Yup.object().shape({
	fields: Yup.object().shape({
		additional: Yup.string(),
		age: Yup.number(),
		alias: Yup.string(),
		approxAge: Yup.boolean(),
		brand: Yup.string(),
		color: Yup.string(),
		criminalAuthority: Yup.string(),
		criminalId: Yup.string(),
		dob: Yup.string(),
		email: Yup.string(),
		eyes: Yup.string(),
		gender: Yup.string(),
		hair: Yup.string(),
		height: Yup.string(),
		idAuthority: Yup.string(),
		idNumber: Yup.string(),
		locality: Yup.string(),
		make: Yup.string(),
		model: Yup.string(),
		name: Yup.string(),
		phone: Yup.string(),
		plate: Yup.string(),
		race: Yup.string(),
		region: Yup.string(),
		size: Yup.string(),
		ss: Yup.string(),
		status: Yup.string(),
		streetAddress: Yup.string(),
		style: Yup.string(),
		type: Yup.string(),
		vin: Yup.string(),
		weight: Yup.number(),
		year: Yup.number()
	}),
	fileUrls: Yup.array().of(Yup.string()),
	primaryImageUrl: Yup.string(),
	tags: Yup.array().of(Yup.object().shape({
		color: Yup.string(),
		id: Yup.string(),
		level: Yup.number(),
		title: Yup.string().required()
	})),
	type: Yup.string().oneOf([
		RecordTypes.Item,
		RecordTypes.Person,
		RecordTypes.Vehicle
	]).nullable().required()
});
var defaultValues = {
	fields: {
		additional: void 0,
		age: void 0,
		alias: void 0,
		approxAge: void 0,
		brand: void 0,
		color: void 0,
		criminalAuthority: void 0,
		criminalId: void 0,
		dob: void 0,
		email: void 0,
		eyes: void 0,
		gender: void 0,
		hair: void 0,
		height: void 0,
		idAuthority: void 0,
		idNumber: void 0,
		locality: void 0,
		make: void 0,
		model: void 0,
		name: void 0,
		phone: void 0,
		plate: void 0,
		race: void 0,
		region: void 0,
		size: void 0,
		ss: void 0,
		status: void 0,
		streetAddress: void 0,
		style: void 0,
		type: void 0,
		vin: void 0,
		weight: void 0,
		year: void 0
	},
	fileUrls: [],
	primaryImageUrl: void 0,
	tags: [],
	type: void 0
};
function RecordForm({ onCancel, onComplete }) {
	const [typeChangeIntention, setTypeChangeIntention] = useState();
	const { rpcService } = useRPCContext();
	const { viewport: { width: viewportWidth } } = useContainerDimensionsContext();
	const isMobileLayout = viewportWidth < Constants.Breakpoint.Sm.width;
	const handleSubmit = async (values, formikHelpers) => {
		if (!values.type) return;
		const recordValues = { record: {
			fields: values.fields,
			fileUrls: values.fileUrls,
			primaryImageUrl: values.primaryImageUrl || DEFAULT_IMAGE_TYPE_MAP[values.type],
			tags: values.tags,
			type: values.type
		} };
		formikHelpers.setSubmitting(true);
		try {
			const response = await rpcService.Records.CreateRecord.v1.call(recordValues);
			onComplete?.(response.data.recordId);
		} catch (err) {
			Alerts.error("There was a problem saving the Record.", err.message);
		} finally {
			formikHelpers.setSubmitting(false);
		}
	};
	return /* @__PURE__ */ jsx(Formik, {
		initialValues: defaultValues,
		onSubmit: handleSubmit,
		validationSchema: ValidationSchema,
		children: ({ isSubmitting, resetForm, setFieldValue, submitForm, values }) => values.type ? /* @__PURE__ */ jsxs("div", {
			className: classnames({ "pt-xs flex-col": isMobileLayout }, "relative flex"),
			children: [
				/* @__PURE__ */ jsxs("div", {
					className: classnames({ "-top-xl z-sticky pr-base sticky w-64 shrink-0 self-start": !isMobileLayout }, "flex flex-col"),
					children: [
						/* @__PURE__ */ jsx("div", {
							className: "relative flex shrink-0 flex-col",
							children: !!values.type && /* @__PURE__ */ jsx(FormikSingleImageFilePicker, {
								defaultImageUrl: DEFAULT_IMAGE_TYPE_MAP[values.type],
								onComplete: (primaryImageUrl) => {
									if (primaryImageUrl && Array.isArray(values.fileUrls) && !values.fileUrls.includes(primaryImageUrl)) setFieldValue("fileUrls", [...values.fileUrls, primaryImageUrl]);
								},
								maxHeight: "100%",
								maxWidth: "100%",
								name: "primaryImageUrl"
							})
						}),
						/* @__PURE__ */ jsx("div", {
							className: "mt-sm flex w-full justify-center",
							children: /* @__PURE__ */ jsxs(ContextMenu, {
								controller: ({ close, isOpen, open }) => /* @__PURE__ */ jsx(Button, {
									disabled: isSubmitting,
									className: "capitalize",
									onClick: isOpen ? close : open,
									subtle: true,
									children: /* @__PURE__ */ jsxs("div", {
										className: "text-md gap-xxs flex items-center text-center font-medium capitalize",
										children: [
											/* @__PURE__ */ jsx(Icon, {
												className: "invisible",
												Icon: MdExpandMore
											}),
											/* @__PURE__ */ jsx("span", { children: values.type }),
											/* @__PURE__ */ jsx(Icon, { Icon: isOpen ? MdExpandLess : MdExpandMore })
										]
									})
								}),
								children: [
									/* @__PURE__ */ jsx(ContextMenu.Option, {
										disabled: values.type === RecordTypes.Item,
										onClick: () => {
											if (values.type !== RecordTypes.Item) setTypeChangeIntention(RecordTypes.Item);
										},
										children: /* @__PURE__ */ jsxs("div", {
											className: "gap-x-base flex items-center",
											children: [/* @__PURE__ */ jsx(Icon, { Icon: MdInventory }), /* @__PURE__ */ jsx("span", {
												className: "capitalize",
												children: RecordTypes.Item
											})]
										})
									}),
									/* @__PURE__ */ jsx(ContextMenu.Option, {
										disabled: values.type === RecordTypes.Person,
										onClick: () => {
											if (values.type !== RecordTypes.Person) setTypeChangeIntention(RecordTypes.Person);
										},
										children: /* @__PURE__ */ jsxs("div", {
											className: "gap-x-base flex items-center",
											children: [/* @__PURE__ */ jsx(Icon, { Icon: BsFillPersonFill }), /* @__PURE__ */ jsx("span", {
												className: "capitalize",
												children: RecordTypes.Person
											})]
										})
									}),
									/* @__PURE__ */ jsx(ContextMenu.Option, {
										disabled: values.type === RecordTypes.Vehicle,
										onClick: () => {
											if (values.type !== RecordTypes.Vehicle) setTypeChangeIntention(RecordTypes.Vehicle);
										},
										children: /* @__PURE__ */ jsxs("div", {
											className: "gap-x-base flex items-center",
											children: [/* @__PURE__ */ jsx(Icon, { Icon: FaCarSide }), /* @__PURE__ */ jsx("span", {
												className: "capitalize",
												children: RecordTypes.Vehicle
											})]
										})
									}),
									/* @__PURE__ */ jsx(ContextMenu.Divider, {}),
									/* @__PURE__ */ jsx("div", {
										className: "text-color-subtle p-xs text-center text-xs",
										children: "Changes will be lost on type switch"
									})
								]
							})
						}),
						/* @__PURE__ */ jsx("div", {
							className: classnames(isMobileLayout ? "mt-sm" : "mt-lg"),
							children: /* @__PURE__ */ jsx(Tags, {
								tags: values.tags,
								max: 10
							})
						})
					]
				}),
				/* @__PURE__ */ jsxs("div", {
					className: classnames({ "border-border border-l": !isMobileLayout }, "flex grow flex-col"),
					children: [/* @__PURE__ */ jsx("div", {
						className: "relative",
						children: !values.type ? /* @__PURE__ */ jsx("div", {
							className: "text-neutral-faded py-xxl px-base text-center text-lg tracking-wide",
							children: "Select the Record's type to continue"
						}) : /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx("div", {
							className: classnames({ "pl-base": !isMobileLayout }, "grow"),
							children: /* @__PURE__ */ jsxs(Tabs, {
								initialTabKey: "info",
								children: [
									/* @__PURE__ */ jsx(Tabs.Tab, {
										title: "Info",
										tabKey: "info",
										children: /* @__PURE__ */ jsx(InfoTab, {
											canEdit: true,
											onFieldChange: ({ fieldName, value }) => setFieldValue(`fields.${fieldName}`, value),
											record: {
												fields: values.fields,
												type: values.type
											}
										})
									}),
									/* @__PURE__ */ jsx(Tabs.Tab, {
										title: "Tags",
										tabKey: "tags",
										children: /* @__PURE__ */ jsx(TagsTab, {})
									}),
									/* @__PURE__ */ jsx(Tabs.Tab, {
										title: "Files",
										tabKey: "files",
										children: /* @__PURE__ */ jsx(FilesTab, {})
									})
								]
							})
						}) })
					}), Boolean(onCancel || values.type) && /* @__PURE__ */ jsx("div", {
						className: "mt-base flex justify-end",
						children: /* @__PURE__ */ jsxs(ButtonGroup, { children: [!!onCancel && /* @__PURE__ */ jsx(Button, {
							disabled: isSubmitting,
							onClick: onCancel,
							children: "Cancel"
						}), !!values.type && /* @__PURE__ */ jsx(Button, {
							loading: isSubmitting,
							loadingMessage: "Submitting",
							onClick: submitForm,
							variant: "main",
							children: "Submit"
						})] })
					})]
				}),
				!!typeChangeIntention && /* @__PURE__ */ jsx(ActionModal, {
					icon: /* @__PURE__ */ jsx(Icon, {
						Icon: IoWarning,
						size: "xl",
						color: "caution"
					}),
					onAction: () => {
						resetForm();
						setFieldValue("type", typeChangeIntention);
					},
					onCancel: () => setTypeChangeIntention(void 0),
					variant: "caution",
					body: /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx("div", {
						className: "text-md font-medium",
						children: "Are you sure you want to continue?"
					}), /* @__PURE__ */ jsx("div", { children: "You will lose all changes made if you change this Record Type." })] })
				})
			]
		}) : /* @__PURE__ */ jsxs("div", { children: [
			/* @__PURE__ */ jsx("div", {
				className: "text-center text-lg font-medium",
				children: "Select Record"
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "mt-lg flex justify-center gap-px",
				children: [
					/* @__PURE__ */ jsx("div", {
						className: "bg-background",
						children: /* @__PURE__ */ jsx(Button, {
							onClick: () => setFieldValue("type", RecordTypes.Item),
							size: "lg",
							outline: true,
							variant: "main",
							children: /* @__PURE__ */ jsxs("div", {
								className: "p-lg gap-xs flex w-36 flex-col items-center",
								children: [/* @__PURE__ */ jsx(Icon, {
									Icon: MdInventory,
									size: "xl"
								}), /* @__PURE__ */ jsx("div", {
									className: "text-color text-md",
									children: "Item"
								})]
							})
						})
					}),
					/* @__PURE__ */ jsx("div", {
						className: "bg-background",
						children: /* @__PURE__ */ jsx(Button, {
							onClick: () => setFieldValue("type", RecordTypes.Person),
							size: "lg",
							outline: true,
							variant: "main",
							children: /* @__PURE__ */ jsxs("div", {
								className: "p-lg gap-xs flex w-36 flex-col items-center",
								children: [/* @__PURE__ */ jsx(Icon, {
									Icon: BsFillPersonFill,
									size: "xl"
								}), /* @__PURE__ */ jsx("div", {
									className: "text-color text-md",
									children: "Person"
								})]
							})
						})
					}),
					/* @__PURE__ */ jsx("div", {
						className: "bg-background",
						children: /* @__PURE__ */ jsx(Button, {
							onClick: () => setFieldValue("type", RecordTypes.Vehicle),
							size: "lg",
							outline: true,
							variant: "main",
							children: /* @__PURE__ */ jsxs("div", {
								className: "p-lg gap-xs flex w-36 flex-col items-center",
								children: [/* @__PURE__ */ jsx(Icon, {
									Icon: FaCarSide,
									size: "xl"
								}), /* @__PURE__ */ jsx("div", {
									className: "text-color text-md",
									children: "Vehicle"
								})]
							})
						})
					})
				]
			}),
			/* @__PURE__ */ jsx("div", {
				className: "mt-lg flex justify-center",
				children: /* @__PURE__ */ jsx(Button, {
					onClick: onCancel,
					children: "Cancel"
				})
			})
		] })
	});
}
//#endregion
export { RecordForm };

//# sourceMappingURL=index.js.map